import { createReducer, on, createFeatureSelector, createSelector, createAction, props } from '@ngrx/store';
import * as TeamsActions from './teams.actions';
import { Team, TeamsPagination } from '../../models/team.model';
import { TeamMember } from '../../../../models/team-member.model';

export interface TeamsState {
  teams: Team[];
  currentTeam: Team | null;
  pagination: TeamsPagination | null;
  loading: boolean;
  error: any;
  totalRecords: number;
  currentPage: number;
  selectedTeam: Team | null;
  createSuccess: boolean;
  teamMembers: { [teamId: string]: TeamMember[] };
  finalReviewers: { [teamId: string]: TeamMember[] };
  financeReviewers: { [teamId: string]: TeamMember[] };
  gembaLeads: { [teamId: string]: TeamMember[] };
  workspaceLeads: { [teamId: string]: TeamMember[] };
  currentFilter: string | null;
}

export const initialTeamsState: TeamsState = {
  teams: [],
  currentTeam: null,
  pagination: null,
  loading: false,
  error: null,
  totalRecords: 0,
  currentPage: 0,
  selectedTeam: null,
  createSuccess: false,
  teamMembers: {},
  finalReviewers: {},
  financeReviewers: {},
  gembaLeads: {},
  workspaceLeads: {},
  currentFilter: null,
};

export const teamsReducer = createReducer(
  initialTeamsState,
  on(TeamsActions.loadTeams, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TeamsActions.loadTeamsSuccess, (state, { pagination, totalItems }) => {
    console.log('teamsReducer: loadTeamsSuccess, new totalItems:', totalItems);
    console.log('teamsReducer: loadTeamsSuccess, received pagination items:', pagination.items);
    return {
      ...state,
      teams: pagination.items, // Ensure the teams array is updated
      loading: false,
      error: null,
      pagination: {
        ...pagination,
        total: totalItems,
      },
      totalRecords: totalItems,
      currentPage: pagination.page,
    };
  }),
  on(TeamsActions.loadTeamsFailure, (state, { error }) => {
    console.error('teamsReducer: loadTeamsFailure', error);
    return {
      ...state,
      loading: false,
      error,
    };
  }),
  on(TeamsActions.loadTeamById, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TeamsActions.loadTeamByIdSuccess, (state, { team }) => ({
    ...state,
    selectedTeam: team,
    loading: false,
    error: null,
  })),
  on(TeamsActions.loadTeamByIdFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(TeamsActions.createTeam, (state) => ({
    ...state,
    loading: true,
    error: null,
    createSuccess: false,
  })),
  on(TeamsActions.createTeamSuccess, (state, { team }) => ({
    ...state,
    loading: false,
    error: null,
    createSuccess: true,
    teams: state.currentPage === 1 ? [team, ...state.teams.slice(0, state.teams.length - 1)] : state.teams,
    totalRecords: state.totalRecords + 1,
  })),
  on(TeamsActions.createTeamFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    createSuccess: false,
  })),
  // Update Team actions
  on(TeamsActions.updateTeam, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TeamsActions.updateTeamSuccess, (state, { team }) => ({
    ...state,
    loading: false,
    error: null,
    teams: state.teams.map((t) => (t.id === team.id ? team : t)),
    selectedTeam: state.selectedTeam && state.selectedTeam.id === team.id ? team : state.selectedTeam,
  })),
  on(TeamsActions.updateTeamFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update Team Finance Settings actions
  on(TeamsActions.updateTeamFinanceSettings, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TeamsActions.updateTeamFinanceSettingsSuccess, (state, { team }) => ({
    ...state,
    loading: false,
    error: null,
    teams: state.teams.map((t) => (t.id === team.id ? team : t)),
    selectedTeam: state.selectedTeam && state.selectedTeam.id === team.id ? team : state.selectedTeam,
  })),
  on(TeamsActions.updateTeamFinanceSettingsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(TeamsActions.resetCurrentTeam, (state) => ({
    ...state,
    selectedTeam: null,
    createSuccess: false,
  })),
  on(TeamsActions.updateFilter, (state, { filter }) => ({
    ...state,
    currentFilter: filter,
  })),
  on(TeamsActions.loadTeamMembersSuccess, (state, { teamId, members }) => ({
    ...state,
    teamMembers: {
      ...state.teamMembers,
      [teamId]: members,
    },
  })),
  on(TeamsActions.addTeamMemberSuccess, (state, { teamId, member }) => {
    const currentMembers = state.teamMembers[teamId] || [];
    return {
      ...state,
      teamMembers: {
        ...state.teamMembers,
        [teamId]: [...currentMembers, member],
      },
    };
  }),
  on(TeamsActions.removeTeamMemberSuccess, (state, { teamId, userId: _userId, membershipId }) => {
    const currentMembers = state.teamMembers[teamId] || [];
    return {
      ...state,
      teamMembers: {
        ...state.teamMembers,
        [teamId]: currentMembers.filter((member) => member.id !== membershipId),
      },
    };
  }),
  // Team Member Actions
  on(TeamsActions.loadTeamMembers, (state) => ({
    ...state,
    loading: true,
  })),
  on(TeamsActions.loadTeamMembersSuccess, (state, { teamId, members }) => {
    // If there's a current team, update its members
    if (state.currentTeam && state.currentTeam.id === teamId) {
      return {
        ...state,
        currentTeam: {
          ...state.currentTeam,
          members: members,
        },
        loading: false,
      };
    }
    return { ...state, loading: false };
  }),
  on(TeamsActions.loadTeamMembersFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(TeamsActions.addTeamMemberSuccess, (state, { teamId, member }) => {
    // If there's a current team, add the member to its members array
    if (state.currentTeam && state.currentTeam.id === teamId) {
      return {
        ...state,
        currentTeam: {
          ...state.currentTeam,
          members: [...(state.currentTeam.members || []), member],
        },
      };
    }
    return state;
  }),
  // Final Reviewer reducers
  on(TeamsActions.loadFinalReviewers, (state) => ({
    ...state,
    loading: true,
  })),
  on(TeamsActions.loadFinalReviewersSuccess, (state, { teamId, finalReviewers }) => ({
    ...state,
    finalReviewers: {
      ...state.finalReviewers,
      [teamId]: finalReviewers,
    },
    loading: false,
  })),
  on(TeamsActions.loadFinalReviewersFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(TeamsActions.addFinalReviewerSuccess, (state, { teamId, finalReviewer }) => {
    const currentFinalReviewers = state.finalReviewers[teamId] || [];
    return {
      ...state,
      finalReviewers: {
        ...state.finalReviewers,
        [teamId]: [...currentFinalReviewers, finalReviewer],
      },
    };
  }),
  on(TeamsActions.removeFinalReviewerSuccess, (state, { teamId, userId }) => {
    const currentFinalReviewers = state.finalReviewers[teamId] || [];
    return {
      ...state,
      finalReviewers: {
        ...state.finalReviewers,
        [teamId]: currentFinalReviewers.filter((reviewer) => reviewer.userId !== Number(userId)),
      },
    };
  }),

  // Finance Reviewer reducers
  on(TeamsActions.loadFinanceReviewers, (state) => ({
    ...state,
    loading: true,
  })),
  on(TeamsActions.loadFinanceReviewersSuccess, (state, { teamId, financeReviewers }) => ({
    ...state,
    financeReviewers: {
      ...state.financeReviewers,
      [teamId]: financeReviewers,
    },
    loading: false,
  })),
  on(TeamsActions.loadFinanceReviewersFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(TeamsActions.addFinanceReviewerSuccess, (state, { teamId, financeReviewer }) => {
    const currentFinanceReviewers = state.financeReviewers[teamId] || [];
    return {
      ...state,
      financeReviewers: {
        ...state.financeReviewers,
        [teamId]: [...currentFinanceReviewers, financeReviewer],
      },
    };
  }),
  on(TeamsActions.removeFinanceReviewerSuccess, (state, { teamId, userId }) => {
    const currentFinanceReviewers = state.financeReviewers[teamId] || [];
    return {
      ...state,
      financeReviewers: {
        ...state.financeReviewers,
        [teamId]: currentFinanceReviewers.filter((reviewer) => reviewer.userId !== Number(userId)),
      },
    };
  }),

  // Gemba Lead reducers
  on(TeamsActions.loadGembaLeads, (state) => ({
    ...state,
    loading: true,
  })),
  on(TeamsActions.loadGembaLeadsSuccess, (state, { teamId, gembaLeads }) => ({
    ...state,
    gembaLeads: {
      ...state.gembaLeads,
      [teamId]: gembaLeads,
    },
    loading: false,
  })),
  on(TeamsActions.loadGembaLeadsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(TeamsActions.addGembaLeadSuccess, (state, { teamId, gembaLead }) => {
    const currentGembaLeads = state.gembaLeads[teamId] || [];
    return {
      ...state,
      gembaLeads: {
        ...state.gembaLeads,
        [teamId]: [...currentGembaLeads, gembaLead],
      },
    };
  }),
  on(TeamsActions.removeGembaLeadSuccess, (state, { teamId, userId }) => {
    const currentGembaLeads = state.gembaLeads[teamId] || [];
    return {
      ...state,
      gembaLeads: {
        ...state.gembaLeads,
        [teamId]: currentGembaLeads.filter((lead) => lead.userId !== Number(userId)),
      },
    };
  }),

  // Workspace Lead reducers
  on(TeamsActions.loadWorkspaceLeads, (state) => ({
    ...state,
    loading: true,
  })),
  on(TeamsActions.loadWorkspaceLeadsSuccess, (state, { teamId, workspaceLeads }) => ({
    ...state,
    workspaceLeads: {
      ...state.workspaceLeads,
      [teamId]: workspaceLeads,
    },
    loading: false,
  })),
  on(TeamsActions.loadWorkspaceLeadsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // Update Team Workspace Management Settings actions
  on(TeamsActions.updateTeamWorkspaceManagementSettings, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TeamsActions.updateTeamWorkspaceManagementSettingsSuccess, (state, { team }) => ({
    ...state,
    loading: false,
    error: null,
    teams: state.teams.map((t) => (t.id === team.id ? team : t)),
    selectedTeam: state.selectedTeam && state.selectedTeam.id === team.id ? team : state.selectedTeam,
  })),
  on(TeamsActions.updateTeamWorkspaceManagementSettingsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }))
);

// For team member actions in teams.actions.ts
export const addTeamMember = createAction('[Teams] Add Team Member', props<{ teamId: number; userId: string; role: string }>());

export const removeTeamMember = createAction('[Teams] Remove Team Member', props<{ teamId: number; userId: string }>());

// Selectors
export const selectTeamsState = createFeatureSelector<TeamsState>('teams');

export const selectAllTeams = (state: TeamsState) => state.teams;

export const selectPagination = createSelector(selectTeamsState, (state) => ({
  total: state.totalRecords,
  page: state.currentPage,
}));

export const selectTeamsLoading = createSelector(selectTeamsState, (state) => state.loading);

export const selectTeamsError = createSelector(selectTeamsState, (state) => state.error);

export const selectTotalTeams = createSelector(selectTeamsState, (state) => state.totalRecords);

export const selectSelectedTeam = createSelector(selectTeamsState, (state) => state.selectedTeam);

export const selectCreateSuccess = createSelector(selectTeamsState, (state) => state.createSuccess);

export const selectCurrentFilter = createSelector(selectTeamsState, (state) => state.currentFilter);

export const getTeamById = (id: string) => (state: TeamsState) => {
  const numericId = Number(id);
  if (state.selectedTeam && state.selectedTeam.id === numericId) {
    return state.selectedTeam;
  }
  return state.teams.find((team) => team.id === numericId) || null;
};

export const getTeamMembers = (teamId: string) => (state: TeamsState) => state.teamMembers[teamId] || [];

export const getFinalReviewers = (teamId: string) => (state: TeamsState) => state.finalReviewers[teamId] || [];

export const getFinanceReviewers = (teamId: string) => (state: TeamsState) => state.financeReviewers[teamId] || [];

export const getGembaLeads = (teamId: string) => (state: TeamsState) => state.gembaLeads[teamId] || [];

export const getWorkspaceLeads = (teamId: string) => (state: TeamsState) => state.workspaceLeads[teamId] || [];
